import React, { useContext, useEffect } from 'react'
import { ContentContainer, MainContainer } from '../../focus_view_shared.styles'
import { UsersInvitePreviewAndExecutionCard } from '../bulk_users_invite_shared'
import { useTranslation } from 'react-i18next'
import { Invitation } from '../bulk_invite_logic'
import { AlertType } from '@amzn/storm-ui/dist/types/Alert/types'
import { useBulkInviteUsers } from '../bulk_invite_users_hook'
import * as i18nKeyList from '../bulk_users_invite_i18n_mapping'
import { getUserManagementURL } from '../../ads_console_utils'
import * as AACChromeAPI from '@amzn/aac-chrome-api'
import { useLoginContext } from 'stagedive/LoginContext'
import { toNullable } from 'fp-ts/es6/Option'
import { StageDiveConfig, StageDiveConfigContext } from '../../../../../../util/app_initialization/appconfig'

interface UsersInviteExecutionProps {
  invitations: Invitation[]
  alertType: AlertType
  onNewInvitesSent: () => void
  setErrors: (errors: string[]) => void
  setUpdateCompleted: () => void
}
export const UsersInviteExecution: React.FunctionComponent<UsersInviteExecutionProps> = (props: UsersInviteExecutionProps): JSX.Element => {
  const { t } = useTranslation()
  const { loading: sendingInvites, errors } = useBulkInviteUsers(props.invitations, props.onNewInvitesSent, () => undefined)
  const { loginContext } = useLoginContext()
  const partnerId = toNullable(loginContext.partnerProfileId) ?? ''
  const stageDiveConfig = useContext<StageDiveConfig>(StageDiveConfigContext)

  useEffect(() => {
    if (errors) {
      props.setErrors(extractEmailsFromErrors(errors))
      props.setUpdateCompleted()
    }
  }, [errors])

  useEffect(() => {
    const chromeConfig = AACChromeAPI.getChromeConfiguration()
    const pageHeader = new AACChromeAPI.PageHeader()
    const objectInfo = new AACChromeAPI.ObjectInfo()
    const actionList = new AACChromeAPI.ItemList()

    chromeConfig.setPageLayoutType('FOCUS')
    const focusModeExitEventId = 'ExitFocusView'

    const sendInvitesButton: AACChromeAPI.BUTTON = new AACChromeAPI.Button()

    const sendInvitesButtonId = 'sendInvites'

    sendInvitesButton.setId(sendInvitesButtonId)
    sendInvitesButton.setLabel(t(i18nKeyList.BULK_INVITE_USERS_SENDING_INVITES_LOADING_LABEL))
    sendInvitesButton.setState(sendingInvites ? AACChromeAPI.ActionState.LOADING : AACChromeAPI.ActionState.ENABLED)
    sendInvitesButton.setButtonType(AACChromeAPI.ButtonType.NORMAL)

    const cancelLink = new AACChromeAPI.Button()
    const cancelLinkId = 'cancel'
    cancelLink.setId(cancelLinkId)
    cancelLink.setLabel(t(i18nKeyList.BULK_INVITE_USERS_CANCEL))
    cancelLink.setButtonType(AACChromeAPI.ButtonType.LINK)

    const cancelLinkFn = (): void => {
      window.location.href = getUserManagementURL(partnerId, stageDiveConfig.hostname)
    }
    AACChromeAPI.getActionEventTarget().addEventListener(cancelLinkId, cancelLinkFn)
    AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, cancelLinkFn)

    actionList.addItem(cancelLink)
    actionList.addItem(sendInvitesButton)

    objectInfo.setActions(actionList)

    pageHeader.setObjectInfo(objectInfo)
    pageHeader.setPageName(t(i18nKeyList.BULK_INVITE_USERS_PAGE_TITLE))
    chromeConfig.setPageHeader(pageHeader)
    AACChromeAPI.setChromeConfiguration(chromeConfig)
    return () => {
      AACChromeAPI.getActionEventTarget().removeEventListener(cancelLinkId, cancelLinkFn)
      AACChromeAPI.getCommonEventTarget().removeEventListener(focusModeExitEventId, cancelLinkFn)
    }
  }, [sendingInvites, t])

  return (
    <MainContainer>
      <ContentContainer>
        <UsersInvitePreviewAndExecutionCard alertType={props.alertType} invitations={props.invitations}></UsersInvitePreviewAndExecutionCard>
      </ContentContainer>
    </MainContainer>
  )
}

const extractEmailsFromErrors = (inputs: Error[]): string[] => {
  return inputs
    .map((input: Error): string | undefined => {
      const match = input.message.match(/email\s*=\s*([\w.-]+@[\w.-]+\.\w+)/)
      return match ? match[1] : undefined
    })
    .flatMap((value) => (!!value ? [value] : []))
}
